import React, { useState } from 'react';
import Glossary, { GlossaryEntry } from '../components/Glossary';
import ComplianceBadges from '../components/ComplianceBadges';

const DS_GLOSSARY: GlossaryEntry[] = [
  { term: 'REDCap', definition: 'Research Electronic Data Capture \u2014 web application for building and managing clinical trial databases and surveys' },
  { term: 'FHIR R4', definition: 'HL7 Fast Healthcare Interoperability Resources Release 4 \u2014 standard for exchanging healthcare data electronically' },
  { term: 'EDC', definition: 'Electronic Data Capture \u2014 system for collecting clinical trial data in digital format' },
  { term: 'API Token', definition: 'Project-level authentication credential for REDCap REST API access' },
  { term: 'Sync', definition: 'Pull records from external system, map to FHIR, and create evidence nodes in D3RI' },
];

interface SyncState {
  loading: boolean;
  lastSync: string | null;
  recordsSynced: number;
  status: 'connected' | 'disconnected' | 'error';
  message: string | null;
}

export default function DataSources() {
  const [redcapSync, setRedcapSync] = useState<SyncState>({
    loading: false, lastSync: '2026-01-25T16:30:00Z', recordsSynced: 42, status: 'connected', message: null,
  });

  const handleRedcapSync = async () => {
    setRedcapSync((s) => ({ ...s, loading: true, message: null }));
    // Simulate sync — in production calls POST /api/v1/integrations/redcap/sync
    await new Promise((r) => setTimeout(r, 2500));
    setRedcapSync((s) => ({
      ...s, loading: false, lastSync: new Date().toISOString(),
      recordsSynced: s.recordsSynced + 6, status: 'connected',
      message: 'Sync complete \u2014 6 new records pulled, 6 FHIR resources created',
    }));
  };

  return (
    <div style={styles.container}>
      <Glossary entries={DS_GLOSSARY} />
      <h2 style={styles.title}>Data Sources</h2>
      <p style={styles.desc}>
        External system connections feeding data into the D3RI evidence graph.
        Each source is mapped to FHIR R4 before ingestion into the bi-temporal schema.
      </p>
      <ComplianceBadges badges={["ICH E6(R3)", "ALCOA", "21 CFR Part 11"]} />

      <div style={styles.cardGrid}>
        {/* REDCap Card */}
        <div style={{ ...styles.card, borderLeft: `4px solid ${redcapSync.status === 'connected' ? '#48bb78' : '#f56565'}` }}>
          <div style={styles.cardHeader}>
            <div>
              <div style={styles.cardName}>REDCap</div>
              <div style={styles.cardType}>Electronic Data Capture</div>
            </div>
            <span style={{
              ...styles.statusBadge,
              backgroundColor: redcapSync.status === 'connected' ? '#22543d' : '#742a2a',
              color: redcapSync.status === 'connected' ? '#48bb78' : '#fc8181',
            }}>
              {redcapSync.status === 'connected' ? 'Connected' : 'Disconnected'}
            </span>
          </div>

          <div style={styles.cardBody}>
            <div style={styles.detailRow}>
              <span style={styles.detailLabel}>API Endpoint</span>
              <code style={styles.codeVal}>localhost:8080/api/</code>
            </div>
            <div style={styles.detailRow}>
              <span style={styles.detailLabel}>Protocol</span>
              <span>REST API + Token Auth</span>
            </div>
            <div style={styles.detailRow}>
              <span style={styles.detailLabel}>Records Synced</span>
              <span style={{ fontWeight: 700 }}>{redcapSync.recordsSynced}</span>
            </div>
            <div style={styles.detailRow}>
              <span style={styles.detailLabel}>Last Sync</span>
              <span>{redcapSync.lastSync ? new Date(redcapSync.lastSync).toLocaleString() : 'Never'}</span>
            </div>
            <div style={styles.detailRow}>
              <span style={styles.detailLabel}>Data Flow</span>
              <span style={{ fontSize: 11 }}>REDCap \u2192 FHIR R4 \u2192 D3RI Evidence Graph</span>
            </div>

            <div style={styles.instruments}>
              <div style={styles.instLabel}>Mapped Instruments:</div>
              <div style={styles.instGrid}>
                {['Vital Signs \u2192 Observation', 'AE Report \u2192 AdverseEvent', 'Lab Result \u2192 DiagnosticReport', 'SAE Report \u2192 AdverseEvent', 'PK Sample \u2192 Specimen'].map((m) => (
                  <span key={m} style={styles.instBadge}>{m}</span>
                ))}
              </div>
            </div>

            <button
              style={{ ...styles.syncBtn, opacity: redcapSync.loading ? 0.7 : 1 }}
              onClick={handleRedcapSync}
              disabled={redcapSync.loading}
            >
              {redcapSync.loading ? (
                <span>Syncing...</span>
              ) : (
                <span>Sync Now</span>
              )}
            </button>

            {redcapSync.message && (
              <div style={styles.syncMsg}>{redcapSync.message}</div>
            )}
          </div>
        </div>

        {/* FHIR Direct Card */}
        <div style={{ ...styles.card, borderLeft: '4px solid #48bb78' }}>
          <div style={styles.cardHeader}>
            <div>
              <div style={styles.cardName}>FHIR R4 Direct</div>
              <div style={styles.cardType}>HL7 FHIR Ingestion</div>
            </div>
            <span style={{ ...styles.statusBadge, backgroundColor: '#22543d', color: '#48bb78' }}>Active</span>
          </div>
          <div style={styles.cardBody}>
            <div style={styles.detailRow}>
              <span style={styles.detailLabel}>Endpoint</span>
              <code style={styles.codeVal}>/api/v1/ingest/fhir</code>
            </div>
            <div style={styles.detailRow}>
              <span style={styles.detailLabel}>Protocol</span>
              <span>POST FHIR R4 Bundle</span>
            </div>
            <div style={styles.detailRow}>
              <span style={styles.detailLabel}>Supported Resources</span>
              <span>Patient, Observation, AdverseEvent, DiagnosticReport, Specimen</span>
            </div>
            <div style={styles.detailRow}>
              <span style={styles.detailLabel}>Data Flow</span>
              <span style={{ fontSize: 11 }}>FHIR Bundle \u2192 Validation \u2192 D3RI Evidence Graph</span>
            </div>
            <div style={styles.detailRow}>
              <span style={styles.detailLabel}>Events Ingested</span>
              <span style={{ fontWeight: 700 }}>19</span>
            </div>
          </div>
        </div>

        {/* Neo4j Card */}
        <div style={{ ...styles.card, borderLeft: '4px solid #48bb78' }}>
          <div style={styles.cardHeader}>
            <div>
              <div style={styles.cardName}>Neo4j AuraDB</div>
              <div style={styles.cardType}>Bi-Temporal Evidence Graph</div>
            </div>
            <span style={{ ...styles.statusBadge, backgroundColor: '#22543d', color: '#48bb78' }}>Connected</span>
          </div>
          <div style={styles.cardBody}>
            <div style={styles.detailRow}>
              <span style={styles.detailLabel}>URI</span>
              <code style={styles.codeVal}>neo4j+s://***</code>
            </div>
            <div style={styles.detailRow}>
              <span style={styles.detailLabel}>Evidence Nodes</span>
              <span style={{ fontWeight: 700 }}>19</span>
            </div>
            <div style={styles.detailRow}>
              <span style={styles.detailLabel}>Decision Twins</span>
              <span style={{ fontWeight: 700 }}>3</span>
            </div>
            <div style={styles.detailRow}>
              <span style={styles.detailLabel}>Hash Chain</span>
              <span style={{ color: '#48bb78', fontWeight: 600 }}>Intact</span>
            </div>
          </div>
        </div>

        {/* Supabase Card */}
        <div style={{ ...styles.card, borderLeft: '4px solid #48bb78' }}>
          <div style={styles.cardHeader}>
            <div>
              <div style={styles.cardName}>Supabase PostgreSQL</div>
              <div style={styles.cardType}>Metadata &amp; Auth Store</div>
            </div>
            <span style={{ ...styles.statusBadge, backgroundColor: '#22543d', color: '#48bb78' }}>Connected</span>
          </div>
          <div style={styles.cardBody}>
            <div style={styles.detailRow}>
              <span style={styles.detailLabel}>URI</span>
              <code style={styles.codeVal}>postgresql+asyncpg://***</code>
            </div>
            <div style={styles.detailRow}>
              <span style={styles.detailLabel}>Users</span>
              <span style={{ fontWeight: 700 }}>3</span>
            </div>
            <div style={styles.detailRow}>
              <span style={styles.detailLabel}>Trials</span>
              <span style={{ fontWeight: 700 }}>1</span>
            </div>
            <div style={styles.detailRow}>
              <span style={styles.detailLabel}>Sites</span>
              <span style={{ fontWeight: 700 }}>3</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  container: { padding: 32 },
  title: { fontSize: 24, fontWeight: 700, color: '#e2e8f0', marginBottom: 4 },
  desc: { color: '#a0aec0', fontSize: 14, marginBottom: 16, maxWidth: 700 },
  cardGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(360px, 1fr))', gap: 16 },
  card: { backgroundColor: '#1a202c', borderRadius: 8, overflow: 'hidden', border: '1px solid #2d3748' },
  cardHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 20px', borderBottom: '1px solid #2d3748' },
  cardName: { color: '#e2e8f0', fontSize: 16, fontWeight: 700 },
  cardType: { color: '#718096', fontSize: 11, marginTop: 2 },
  statusBadge: { padding: '3px 12px', borderRadius: 12, fontSize: 11, fontWeight: 700 },
  cardBody: { padding: '12px 20px 20px' },
  detailRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 0', borderBottom: '1px solid #171923', color: '#e2e8f0', fontSize: 13 },
  detailLabel: { color: '#718096', fontSize: 11, fontWeight: 600 },
  codeVal: { color: '#63b3ed', fontSize: 11, fontFamily: 'monospace', backgroundColor: '#171923', padding: '2px 8px', borderRadius: 4 },
  instruments: { marginTop: 12 },
  instLabel: { color: '#718096', fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 },
  instGrid: { display: 'flex', flexWrap: 'wrap', gap: 6 },
  instBadge: { backgroundColor: '#171923', color: '#a0aec0', padding: '3px 8px', borderRadius: 4, fontSize: 10, border: '1px solid #2d3748' },
  syncBtn: {
    marginTop: 16, width: '100%', padding: '10px 0',
    backgroundColor: '#2b6cb0', color: '#fff', border: 'none', borderRadius: 6,
    cursor: 'pointer', fontSize: 13, fontWeight: 600,
  },
  syncMsg: { marginTop: 8, padding: '8px 12px', backgroundColor: 'rgba(72, 187, 120, 0.1)', border: '1px solid #22543d', borderRadius: 6, color: '#48bb78', fontSize: 12 },
};
